/*
 *************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 *************************************************************************
 */

// Since this code is initial bundle of site, we need to keep its size as small as possible.
/* eslint-disable no-console */
import {skeletonState} from './SkeletonState';
import {getAppsUISkeleton} from '@nest/applet-apps/src/view/AppsSkeletonUI';
import {getCookie} from '@nest/utility/src/Cookie';
import {ThemeUtils} from '@nest/utility/src/ThemeUtils';
import {BrowserUtils} from '@nest/utility/src/BrowserUtils';
import {OpenUtils} from '@nest/utility/src/OpenUtils';
import {getSelectedBackground} from '@adobehome/home-ui/backgrounds';
import {supportedLanguages} from './constants/LanguageConstants';
import {HomeSkeleton, SideNavSkeleton} from '@adobehome/home-ui/skeleton';
import {BenefitsSkeleton} from '@nest/applet-benefits/src/view/skeleton/BenefitsSkeleton';
import {PlansSkeleton} from '@nest/applet-plans/src/view/skeleton/PlansSkeleton';
import {AssistantSkeleton} from '@nest/applet-assistant/src/view/skeleton/AssistantSkeleton';
import {PERFORMANCE_MARKS} from '@nest/utility/src/performance-monitor/performanceMarks';
import {Utils} from './Utils';
import './index.scss';
// need to add this as Home v2 skeleton breaks without it
import '@react-spectrum/s2/page.css';

// Skeleton Loading

const toFragment = (htmlTemplate: any) => {
	const fragment = document.createRange().createContextualFragment(htmlTemplate)
		.children[0];
	return fragment;
};

const themeCookie = getCookie('creative-cloud-theme') || 'device';
const theme = themeCookie === 'dark' ? 'darkest' : themeCookie;
const themeMode =
	themeCookie === 'device' ? ThemeUtils.getSystemTheme() : theme;

const nestTheme = themeMode === 'darkest' ? 'dark' : 'light';
document.documentElement.dataset['nestTheme'] = nestTheme;

// setting the theme for TAB once theme is available
try {
	// TODO: Handle promise rejection properly (add error handling or await as needed). See https://git.corp.adobe.com/nest/nest/blob/main/docs/best-practices/no-floating-promises.md
	// eslint-disable-next-line @typescript-eslint/no-floating-promises
	window.isTopAppBarJsLoaded?.then(() => {
		window.TopAppBar?.updateTheme(themeMode === 'darkest' ? 'dark' : 'light');
	});
} catch (error) {
	console.error('Failed to update theme for TopAppBar', error);
}

// we're not allowed to load React and react-router here, so we reimplement routing.
function getRouteSkeletonData(): {skeletonString: string; appRoute: string} {
	const currentPath = window.location.pathname;
	if (currentPath.startsWith('/apps')) {
		return {skeletonString: getAppsUISkeleton(), appRoute: 'apps'};
	} else if (currentPath.startsWith('/benefits')) {
		return {
			skeletonString: BenefitsSkeleton.toStringV2(),
			appRoute: 'benefits',
		};
	} else if (currentPath.startsWith('/home/plans')) {
		return {skeletonString: PlansSkeleton.toString(), appRoute: 'plans'};
	} else if (currentPath.startsWith('/home/search')) {
		return {skeletonString: getAppsUISkeleton(), appRoute: 'search'};
	} else if (currentPath.startsWith('/home/assistant')) {
		return {
			skeletonString: AssistantSkeleton.toString(),
			appRoute: 'assistant',
		};
	} else if (currentPath.startsWith('/home')) {
		const backgroundUrl = getSelectedBackground(nestTheme);
		return {
			skeletonString: new HomeSkeleton({
				theme: nestTheme,
				background: backgroundUrl,
			}).toString(),
			appRoute: 'home',
		};
	} else if (currentPath.startsWith('/files')) {
		return {skeletonString: getAppsUISkeleton(), appRoute: 'files'};
	} else if (currentPath.startsWith('/create')) {
		return {skeletonString: getAppsUISkeleton(), appRoute: 'create'};
	} else if (currentPath.startsWith('/manage-team')) {
		return {skeletonString: getAppsUISkeleton(), appRoute: 'manage-team'};
	}
	return {skeletonString: getAppsUISkeleton(), appRoute: 'default'};
}

// custom event that must be emitted by router, on route change
window.addEventListener('update-nest-app-route', () => {
	const {appRoute} = getRouteSkeletonData();
	// on SPA navigate, need to update page-level styles
	// see index.scss
	document.documentElement.dataset['nestAppRoute'] = appRoute;
});

const {appRoute, skeletonString: appletSkeletonFragment} =
	getRouteSkeletonData();
document.documentElement.dataset['nestAppRoute'] = appRoute;

const headerSkeleton = `<div class='sk-header-wrapper'>
	<div class='sk-wiper-on-layout sk-logo'></div>
	<div class='sk-unav'>
		${Array(4).fill("<div class='sk-wiper-on-layout sk-unav-item'></div>").join('')}
	</div>
</div>`;

const sidenavSkeleton = new SideNavSkeleton({theme: nestTheme}).toString();

const appSkeletonFragment =
	toFragment(`<div class='skeleton-wrapper ${themeMode !== '' ? `spectrum--${themeMode}` : ''}'>
		<div class='sk-header'>${headerSkeleton}</div>
		<div class='sk-sidenav'>${sidenavSkeleton}</div>
		<div class='sk-body'>${appletSkeletonFragment ? appletSkeletonFragment : ''}</div>
	</div>`);

const skeletonEl = document.querySelector<HTMLDivElement>('#skeleton');
if (appSkeletonFragment) {
	skeletonEl?.appendChild(appSkeletonFragment);
	performance.mark(PERFORMANCE_MARKS.skeleton);
}

skeletonState.registerObserver((showSkeleton: boolean) => {
	// hide skeleton and show actual UI
	const rootEl = document.querySelector<HTMLDivElement>('#root');
	if (rootEl) {
		rootEl.style.display = showSkeleton ? 'none' : 'block';
	}
	if (skeletonEl) {
		skeletonEl.style.display = showSkeleton ? 'block' : 'none';
	}
});

// setTimeout is needed so below code is executed in next event loop cycle as a seperate task on main thread
setTimeout(() => {
	// Error Checking
	/* eslint-disable camelcase */
	// NOTE: if this object needs to be changed, also change it in error.js
	const internationalLangLocaleMap = {
		ae_en: 'en',
		au: 'en',
		be_en: 'en',
		ca: 'en',
		eg_en: 'en',
		gr_en: 'en',
		hk_en: 'en',
		id_en: 'en',
		ie: 'en',
		il_en: 'en',
		in: 'en',
		kw_en: 'en',
		lu_en: 'en',
		my_en: 'en',
		ng: 'en',
		nz: 'en',
		ph_en: 'en',
		qa_en: 'en',
		sa_en: 'en',
		sg: 'en',
		th_en: 'en',
		us: 'en',
		vn_en: 'en',
		africa: 'en',
		cis_en: 'en',
		mena_en: 'en',
		sea: 'en',
		ar: 'en',
		cr: 'es',
		cz: 'cs',
		dk: 'da',
		de: 'de',
		at: 'de',
		ch_de: 'de',
		lu_de: 'de',
		cl: 'es',
		co: 'es',
		ec: 'es',
		gt: 'es',
		la: 'es',
		mx: 'es',
		pe: 'es',
		pr: 'es',
		es: 'es',
		fi: 'fi',
		fr: 'fr',
		be_fr: 'fr',
		ca_fr: 'fr',
		ch_fr: 'fr',
		lu_fr: 'fr',
		hu: 'hu',
		id: 'id',
		ch_it: 'it',
		it: 'it',
		jp: 'ja',
		kr: 'ko',
		no: 'nb',
		be_nl: 'nl',
		nl: 'nl',
		pl: 'pl',
		br: 'pt',
		pt: 'pt',
		ru: 'ru',
		cis_ru: 'ru',
		se: 'sv',
		th: 'th',
		tr: 'tr',
		uk: 'uk',
		vi: 'vi',
		cn: 'zh-Hans',
		tw: 'zh-Hant',
		hk_zh: 'zh-Hant',
	};

	/* eslint-enable camelcase */

	// get ?locale query param value from URL
	const url = new URL(window.location.href);

	let validParams = '';

	url.searchParams.forEach((value, key) => {
		let isRelevantKey = false;

		if (
			key === 'acomLocale' &&
			Object.keys(internationalLangLocaleMap).includes(value)
		) {
			isRelevantKey = true;
		}

		if (key === 'locale' && supportedLanguages.includes(value)) {
			isRelevantKey = true;
		}

		if (isRelevantKey) {
			if (validParams.length) {
				validParams += '&';
			}
			validParams += `${key}=${value}`;
		}
	});

	if (validParams.length) {
		validParams = `?${validParams}`;
	}

	// if cookie is disabled
	if (!BrowserUtils.isCookieEnabled()) {
		console.info(
			`[pre-starter] Cookies  Disabled: Redirecting to error page. UserAgent: ${window.navigator.userAgent}`,
		);
		OpenUtils.openLink(
			`/home/errors/cookieDisabled.html${validParams}`,
			'_self',
		);
	}
	// if unsupported browser is detected
	else if (!BrowserUtils.isSupportedBrowser()) {
		console.info(
			`[pre-starter] Unsupported browser: Redirecting to error page. UserAgent: ${window.navigator.userAgent}`,
		);

		OpenUtils.openLink(
			`/home/errors/unsupportedBrowser.html${validParams}`,
			'_self',
		);
	} else {
		// init IMS call
		const identiyProviderPromise = import(
			/* webpackChunkName: "identityProvider" */ './loaders/identityLoader'
		).then(({loadIdentityProvider}) => loadIdentityProvider());
		const environment = Utils.getEnvironment();
		// Environment check here to set correct OneTrust Domain ID
		const privacyOtDomainId =
			environment === 'prod'
				? '7a5eb705-95ed-4cc4-a11d-0cc5760e93db'
				: '7a5eb705-95ed-4cc4-a11d-0cc5760e93db-test';
		window.fedsConfig = {
			...(window.fedsConfig || {}),
			privacy: {
				...(window.fedsConfig?.privacy || {}),
				otDomainId: privacyOtDomainId,
				footerLinkSelector: '.footer-container .consent-link-hidden',
			},
		};
		const script: any = document.createElement('script');
		script.src =
			'https://www.adobe.com/etc.clientlibs/globalnav/clientlibs/base/privacy-standalone.js';
		script.async = true;
		script.setAttribute('id', 'onetrust-privacy-script');
		if (document.body) {
			document.body.appendChild(script);
		}

		const handleError = (e: unknown) => {
			console.error('[pre-starter] Failed to load Adobe Home', e);
			OpenUtils.openLink(`/home/errors/5XX.html${validParams}`, '_self');
		};

		import(/* webpackChunkName: "adobe-home-starter" */ './Starter')
			.then(({default: init}) => {
				init(identiyProviderPromise).catch((e) => {
					handleError(e);
				});
			})
			.catch((e) => {
				handleError(e);
			});
	}
}, 0);
